import type { PrivacyNoticeDocument } from '../../domain/types';

/**
 * Contact address for privacy matters: ARCO requests, opting out of the secondary purposes and
 * limiting the use of personal data. The notice names it four times, so it is declared once.
 */
const PRIVACY_EMAIL = 'administracion@parrotsoftware.io';
const PRIVACY_EMAIL_HREF = `mailto:${PRIVACY_EMAIL}`;

/**
 * Aviso de Privacidad Integral (LFPDPPP, México), served at `/legal/terms`.
 *
 * Verbatim transcription of the text Legal issued on 18 September 2026 — its quotes, numbering and
 * punctuation are left as they came, and only plain misspellings are fixed.
 */
export const PRIVACY_NOTICE: PrivacyNoticeDocument = {
  id: 'privacy_notice',
  version: '1.0.0',
  effectiveDate: '2026-09-18',
  country: 'MX',
  preamble: [
    'A fin de dar cumplimiento con lo establecido en la Ley Federal de Protección de Datos Personales en Posesión de los Particulares y demás lineamientos aplicables, S3 Software, S.A. de C.V. (en adelante, “Parrot”) a través de su página web ',
    { text: 'https://parrotsoftware.com.mx', href: 'https://parrotsoftware.com.mx' },
    ' (el “Sitio”), y sus aplicaciones Parrot POS, Parrot KDS, Parrot Pay y App Reportes (conjuntamente, los “Productos”) y demás plataformas digitales y aplicaciones web y/o móviles presentes y futuras de su propiedad, pone a su disposición el presente:',
  ],
  title: 'Aviso de Privacidad Integral',
  lead:
    'Con la finalidad de dar un tratamiento legítimo, controlado e informado a sus Datos Personales, que actualmente o en el futuro nos proporcione y que obren en nuestras bases de datos, o que hayan sido recopilados por cookies, o cualquier otra tecnología de seguimiento web, siendo Parrot la Persona Responsable del uso y protección de sus Datos Personales. El presente Aviso de Privacidad se encuentra regulado por los lineamientos de la Ley Federal de Protección de Datos Personales en Posesión de los Particulares, publicada en el Diario Oficial de la Federación el 20 de marzo de 2025.',
  intro: [
    {
      kind: 'subsection',
      title: 'Domicilio e Información de Contacto de la Persona Responsable',
      blocks: [
        {
          kind: 'paragraph',
          text:
            'Parrot señala como domicilio para oír, recibir notificaciones, atender solicitudes de ejercicio de Derechos ARCO y limitar el uso o divulgación de Datos Personales, el siguiente:',
        },
        {
          kind: 'callout',
          lines: [
            { text: 'Parrot', emphasis: true },
            { text: 'Londres 253 101. Col. Juárez. Cuauhtémoc. 06600. CDMX, México.' },
            {
              text: ['Correo electrónico: ', { text: PRIVACY_EMAIL, href: PRIVACY_EMAIL_HREF }],
              emphasis: true,
            },
          ],
        },
        {
          kind: 'paragraph',
          emphasis: true,
          text:
            'El domicilio anteriormente señalado constituye el punto central de contacto para todo lo relacionado con la protección de Datos Personales recabados por Parrot. La Persona Titular reconoce que cualquier comunicación relacionada con sus Datos Personales deberá remitirse a través de estos medios oficiales para poder garantizar un seguimiento adecuado y documentado.',
        },
        {
          kind: 'paragraph',
          text:
            'Parrot pone sus Productos a disposición de restaurantes y otros establecimientos que los contratan (cada uno, un “Cliente”). Por ello, Parrot trata Datos Personales bajo dos caracteres distintos:',
        },
        {
          kind: 'list',
          items: [
            {
              text:
                'Como Persona Responsable, respecto de los Datos Personales de sus Clientes, de las Personas Usuarias que estos dan de alta en los Productos, de sus proveedores y de sus candidatos y personas empleadas. El presente Aviso de Privacidad rige dicho tratamiento.',
            },
            {
              text:
                'Como Persona Encargada, respecto de los Datos Personales de los comensales y demás terceros que el Cliente captura o genera a través de los Productos. En estos supuestos el Cliente mantiene el carácter de Persona Responsable, y es quien determina las finalidades y los medios del tratamiento, por lo que dicho tratamiento se rige por el aviso de privacidad del Cliente.',
            },
          ],
        },
      ],
    },
  ],
  sections: [
    {
      id: 'personal_data',
      title: '1. Datos Personales',
      group: 1,
      blocks: [
        {
          kind: 'paragraph',
          text:
            'Parrot podrá recabar y tratar diferentes tipos de Datos Personales de la Persona Titular, ya sea de manera directa, indirecta o automática, según la relación que esté establecida entre Parrot y la Persona Titular.',
        },
        { kind: 'paragraph', text: 'Parrot puede obtener los siguientes Datos Personales de la Persona Titular:' },
        {
          kind: 'list',
          items: [
            {
              lead: 'Datos de Contacto e Identificación:',
              text:
                'nombre completo, correo electrónico, teléfono, cargo, sucursales asignadas y domicilio comercial de la Persona Usuaria o del contacto designado por el Cliente.',
            },
            {
              lead: 'Datos Corporativos y Fiscales:',
              text:
                'razón o denominación social, nombre comercial, RFC, régimen fiscal, Constancia de Situación Fiscal y domicilio fiscal del Cliente.',
            },
            {
              lead: 'Datos de Verificación de Identidad de Parrot Pay:',
              text:
                'estado de cuenta bancario, CLABE interbancaria, institución financiera y nombre de la persona titular de la cuenta bancaria.',
            },
            {
              lead: 'Datos de Acceso y Autenticación:',
              text:
                'correo electrónico, teléfono, contraseña almacenada de forma cifrada, identificador de sesión, registros de verificación y bitácoras de acceso.',
            },
            {
              lead: 'Datos Técnicos y de Uso:',
              text: 'identificador del dispositivo y de instalación y dirección IP.',
            },
            {
              lead: 'Datos de Candidatos y Personas Empleadas:',
              text:
                'nombre completo, currículum, teléfono, correo electrónico, domicilio, fecha de nacimiento, estado civil, grado de estudios, experiencia laboral, RFC, CURP, número de seguridad social y datos de contactos de emergencia y de dependientes económicos.',
            },
          ],
        },
      ],
    },
    {
      id: 'processing',
      title: '2. Tratamiento de Datos Personales',
      group: 1,
      blocks: [
        {
          kind: 'paragraph',
          text:
            'Parrot garantiza que todo tratamiento de los Datos Personales será realizado de manera legítima, controlada e informada. El tratamiento se llevará a cabo conforme a los principios de licitud, finalidad, lealtad, consentimiento, calidad, proporcionalidad, información y responsabilidad, según lo establecido por el artículo 5 de la LFPDPPP.',
        },
        {
          kind: 'paragraph',
          text:
            'En cuanto a la conservación, bloqueo y supresión de datos, Parrot conservará los Datos Personales únicamente durante el tiempo necesario para cumplir con las finalidades para las cuales fueron recabados, cumpliendo en todo momento con los lineamientos de la Ley. Los plazos aplicables son:',
        },
        {
          kind: 'list',
          items: [
            {
              lead: 'Periodo de Tratamiento y Conservación:',
              text:
                'Los Datos Personales serán conservados en nuestras Bases de Datos mientras persista la finalidad para la cual fueron recabados y, adicionalmente, por el plazo que exijan las disposiciones fiscales, laborales y de prevención del fraude aplicables.',
            },
            {
              lead: 'Periodo de Bloqueo:',
              text:
                'Una vez cumplida la finalidad que motivó su tratamiento y cuando dejen de ser necesarios según el párrafo anterior, los Datos Personales serán suprimidos previo bloqueo.',
            },
            {
              lead: 'Supresión o Desecho:',
              text:
                'Transcurrido el periodo de bloqueo, los Datos Personales serán suprimidos y se procederá a su cancelación en la base de datos correspondiente mediante su destrucción segura para asegurar que no puedan asociarse a la Persona Titular ni permitir su identificación posterior.',
            },
          ],
        },
      ],
    },
    {
      id: 'purposes',
      title: '3. Finalidades del Tratamiento',
      group: 2,
      blocks: [
        {
          kind: 'paragraph',
          text:
            'Parrot tratará los Datos Personales de la Persona Titular únicamente para los fines que resulten necesarios, proporcionales y relacionados con la relación establecida entre las partes.',
        },
        {
          kind: 'paragraph',
          emphasis: true,
          text:
            'Las finalidades para las cuales se utilizan los Datos Personales serán clasificadas en primarias, las cuales serán indispensables para la relación entre Parrot y la Persona Titular, al igual que para el cumplimiento de obligaciones legales y contractuales; y secundarias, que permitan mejorar la relación entre Parrot y la Persona Titular.',
        },
        {
          kind: 'subsection',
          title: '3.1 Finalidades Primarias',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Las finalidades primarias para el tratamiento de los Datos Personales incluyen las siguientes:',
            },
            {
              kind: 'list',
              items: [
                {
                  text:
                    'Identificar y validar la identidad de la Persona Titular, verificar el correo electrónico y el teléfono con los que se crea la cuenta y proteger el acceso a los Productos.',
                },
                {
                  text:
                    'Crear, configurar y administrar la cuenta del Cliente, sus sucursales y sus Personas Usuarias y habilitar los Productos contratados.',
                },
                {
                  text:
                    'Verificar el alta del Cliente ante el Servicio de Administración Tributaria y cumplir los requisitos de identificación que exige el procesador de pagos para habilitar el servicio de cobros de Parrot Pay.',
                },
                { text: 'Procesar el cobro de la suscripción, de los add-ons y de los cargos por consumo.' },
                {
                  text:
                    'Procesar los cobros efectuados a través de Parrot Pay, dispersar al Cliente el importe de sus ventas, conciliar las operaciones y atender devoluciones, contracargos y aclaraciones.',
                },
                {
                  text:
                    'Prevenir el fraude y el uso indebido de los Productos, investigar incidentes de seguridad y atender el deber de informar las vulneraciones que afecten de forma significativa los derechos patrimoniales o morales de la Persona Titular.',
                },
                {
                  text:
                    'Brindar soporte técnico y capacitación, atender solicitudes e incidentes y mantener la estabilidad de los Productos.',
                },
                { text: 'Llevar a cabo los procesos de reclutamiento y selección y gestionar la relación laboral.' },
                {
                  text:
                    'Cumplir con las obligaciones legales, fiscales, laborales y contractuales a cargo de Parrot y atender los requerimientos de las autoridades.',
                },
                {
                  text:
                    'Gestionar el abasto de insumos del Cliente, generando y transmitiendo órdenes de reabastecimiento derivadas del consumo de los comensales.',
                },
              ],
            },
          ],
        },
        {
          kind: 'subsection',
          title: '3.2 Finalidades Secundarias',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Las finalidades secundarias para el tratamiento de los Datos Personales incluyen las siguientes:',
            },
            {
              kind: 'list',
              items: [
                {
                  text:
                    'Envío de información comercial, promocional o publicitaria relacionada con productos, eventos corporativos o vacantes laborales dentro de Parrot.',
                },
                {
                  text:
                    'Realizar estudios, análisis y encuestas de satisfacción y calidad para la mejora continua de los Productos ofrecidos por Parrot.',
                },
                {
                  text:
                    'Conservar el currículum de las personas candidatas no seleccionadas para considerarlas en vacantes futuras.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: [
                'La Persona Titular podrá manifestar su negativa al tratamiento de sus Datos Personales para las finalidades secundarias enviando un correo electrónico a ',
                { text: PRIVACY_EMAIL, href: PRIVACY_EMAIL_HREF },
                '.',
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'transfers',
      title: '4. Transferencia de Datos Personales a Terceros',
      group: 3,
      blocks: [
        {
          kind: 'paragraph',
          text:
            'Una Transferencia de Datos Personales es toda comunicación de Datos Personales realizada a una persona física o moral, nacional o extranjera que sea distinta de la Persona Titular, de la Persona Responsable o de la Persona Encargada del tratamiento.',
        },
        {
          kind: 'paragraph',
          text:
            'Parrot se compromete a comunicar a estos terceros el contenido del Aviso de Privacidad. No constituyen Transferencia las remisiones de Datos Personales a los proveedores que los tratan por su cuenta en su carácter de Persona Encargada.',
        },
        {
          kind: 'subsection',
          title: '4.1 Transferencias sin requisito de consentimiento',
          blocks: [
            {
              kind: 'paragraph',
              text:
                'De conformidad con el Artículo 36 de la LFPDPPP, sus Datos Personales podrán ser transferidos sin requerir su consentimiento en los siguientes supuestos:',
            },
            {
              kind: 'list',
              items: [
                {
                  lead: 'Transferencias internas:',
                  text:
                    'Se podrán transferir datos a sociedades controladoras, subsidiarias o afiliadas nacionales o internacionales bajo el control común de Parrot.',
                },
                {
                  lead: 'Cumplimiento de Obligaciones Legales o Contractuales:',
                  text:
                    'Cuando la transferencia sea precisa para el mantenimiento o cumplimiento de la relación jurídica entre Parrot y la Persona Titular, o para ejercer un derecho o cumplir obligaciones derivadas de dicha relación.',
                },
                {
                  lead: 'Autoridades Judiciales y de Interés Público:',
                  text:
                    'Cuando la transferencia sea necesaria o legalmente exigida para el reconocimiento, ejercicio o defensa de un derecho en un proceso judicial o para la salvaguarda de un interés público o para la administración de justicia.',
                },
                {
                  lead: 'Salud y Servicios Sanitarios:',
                  text:
                    'Cuando la transferencia sea necesaria para la prevención, diagnóstico médico, la prestación de asistencia sanitaria o la gestión de servicios sanitarios.',
                },
              ],
            },
          ],
        },
        {
          kind: 'subsection',
          title: '4.2 Transferencias que requieren su consentimiento',
          blocks: [
            {
              kind: 'paragraph',
              text:
                'Para cualquier transferencia que no se encuentre en los supuestos de excepción anteriormente mencionados, y aquellos permitidos por la Ley, será requerido el consentimiento expreso de la Persona Titular.',
            },
            {
              kind: 'paragraph',
              text:
                'La Persona Titular deberá manifestar si no acepta la transferencia de sus datos a terceros para las finalidades secundarias.',
            },
          ],
        },
      ],
    },
    {
      id: 'use_limitation',
      title: '5. Limitación del Uso o Divulgación de Datos Personales',
      group: 3,
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'En caso de que lo desee, la Persona Titular podrá solicitar limitaciones adicionales al uso o divulgación de sus Datos Personales enviando una solicitud al correo ',
            { text: PRIVACY_EMAIL, href: PRIVACY_EMAIL_HREF },
            ', la cual debe incluir su información de contacto y una descripción clara y precisa de los Datos Personales y de las finalidades cuyo uso o divulgación desea limitar. Al presentar esta solicitud, la Persona Titular acepta que, si los Datos Personales cuya limitación se solicita son esenciales y necesarios para mantener la relación existente con Parrot, esta relación podría ser terminada.',
          ],
        },
      ],
    },
    {
      id: 'arco_rights',
      title: '6. Derechos ARCO',
      group: 4,
      blocks: [
        {
          kind: 'paragraph',
          text:
            'De conformidad con lo establecido en la LFPDPPP, la Persona Titular tiene el derecho de Acceder, Rectificar, Cancelar u Oponer el tratamiento de sus Datos Personales. Parrot pone a disposición de la Persona Titular los siguientes mecanismos para que puedan ser ejercidos de manera adecuada, transparente y gratuita, conforme a los plazos establecidos en la LFPDPPP.',
        },
        {
          kind: 'subsection',
          title: '6.1 Medios de ejercicio de los Derechos ARCO',
          blocks: [
            {
              kind: 'paragraph',
              emphasis: true,
              text:
                'La Persona Titular podrá solicitar el Acceso a sus Datos Personales para conocer cuál es la información de la Persona Titular que Parrot tiene en su posesión, al igual que el origen de estos Datos Personales, las condiciones y finalidades del Tratamiento de estos, así como las transferencias a terceros realizadas y previstas. De igual manera, podrá solicitar la Rectificación de sus Datos Personales en caso de que estos resulten inexactos, incompletos, desactualizados o incorrectos. Asimismo, la Persona Titular podrá solicitar la Cancelación de sus Datos Personales y la Oposición del uso de sus Datos Personales en caso de que estos no sean necesarios para las finalidades establecidas en el presente Aviso de Privacidad.',
            },
          ],
        },
        {
          kind: 'subsection',
          title: '6.2 Procedimiento de ejercicio de los Derechos ARCO',
          blocks: [
            {
              kind: 'paragraph',
              text:
                'Parrot pone a disposición de la Persona Titular los mecanismos, medios y procedimientos necesarios para ejercer sus Derechos ARCO. La persona o departamento que dará trámite a las solicitudes es Tecnología',
            },
            {
              kind: 'paragraph',
              text:
                'La Persona Titular deberá presentar la solicitud por escrito al Departamento de Tecnología de Parrot ya sea por medio físico o electrónico, conforme a las opciones de contacto proporcionadas en este Aviso de Privacidad. La Solicitud deberá contener y acompañar, obligatoriamente, los siguientes elementos.',
            },
            {
              kind: 'list',
              items: [
                {
                  text:
                    'El nombre de la Persona Titular y su domicilio o cualquier otro medio (correo electrónico) para recibir notificaciones de la respuesta.',
                },
                { text: 'Los documentos que acrediten la identidad de la Persona Titular (identificación oficial).' },
                {
                  text:
                    'La descripción clara y precisa de los Datos Personales respecto de los que se busca ejercer alguno de los Derechos ARCO.',
                },
                { text: 'La descripción del derecho ARCO que se pretende ejercer, o la solicitud concreta.' },
                { text: 'Cualquier otro elemento o documento que facilite la localización de los Datos Personales.' },
              ],
            },
            {
              kind: 'paragraph',
              text:
                'En caso de solicitar la Rectificación de Datos Personales, la Persona Titular deberá, además, indicar las modificaciones a realizarse y adjuntar la documentación que sustente y compruebe su petición.',
            },
          ],
        },
        {
          kind: 'subsection',
          title: '6.3 Información de contacto para ejercicio de Derechos ARCO',
          blocks: [
            {
              kind: 'paragraph',
              text: [
                'Para ejercer cualquiera de los Derechos ARCO y realizar consultas o aclaraciones sobre el tratamiento de sus Datos Personales, la Persona Titular deberá contactar a ',
                { text: PRIVACY_EMAIL, href: PRIVACY_EMAIL_HREF },
                ' o presentar su solicitud en el domicilio indicado en el presente Aviso de Privacidad.',
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'cookies',
      title: '7. Cookies y Tecnologías de Rastreo',
      group: 5,
      blocks: [
        {
          kind: 'paragraph',
          emphasis: true,
          // The source text closes with a sentence pointing at a cookie policy through a `[LINK]`
          // marker. That policy does not exist, so the sentence is dropped rather than published
          // with a link that leads nowhere.
          text:
            'Parrot utiliza cookies para ofrecerle la mejor experiencia posible cuando utiliza el Sitio. Las cookies son archivos de texto que se guardan en el navegador de internet o que este guarda en el terminal del usuario. Parrot puede utilizar cookies para recabar información sobre el uso del Sitio.',
        },
      ],
    },
    {
      id: 'security',
      title: '8. Medidas de Seguridad',
      group: 5,
      blocks: [
        {
          kind: 'paragraph',
          text:
            'Parrot ha establecido y mantiene medidas de seguridad administrativas, técnicas y físicas que permiten proteger los Datos Personales contra daño, pérdida, alteración, destrucción o el uso, acceso o tratamiento no autorizado. Estas medidas de seguridad están orientadas a asegurar un nivel de protección de los Datos Personales conforme al riesgo, asegurando la confidencialidad, integridad y disponibilidad de los sistemas de tratamiento, así como el establecimiento de controles que obligan a todas las personas involucradas en el tratamiento de datos a mantener la confidencialidad.',
        },
      ],
    },
    {
      id: 'changes',
      title: '9. Cambios al Aviso de Privacidad',
      group: 5,
      blocks: [
        {
          kind: 'paragraph',
          text:
            'Parrot se reserva el derecho de efectuar modificaciones o actualizaciones al presente Aviso de Privacidad en cualquier momento.',
        },
        {
          kind: 'subsection',
          title: '9.1 Medios de notificación',
          blocks: [
            {
              kind: 'paragraph',
              text:
                'La Persona Titular de los Datos Personales será informada sobre los cambios o modificaciones que sufra el presente Aviso de Privacidad a través de los siguientes medios:',
            },
            {
              kind: 'list',
              items: [
                { text: 'Mediante publicaciones visibles en el Sitio.' },
                {
                  text: 'A través de correo electrónico enviado a la dirección de contacto que nos haya proporcionado.',
                },
              ],
            },
          ],
        },
        {
          kind: 'subsection',
          title: '9.2 Última fecha de actualización',
          blocks: [
            // The source text carries a `__/__/___` marker, resolved with the date the notice was issued.
            {
              kind: 'paragraph',
              text: 'Este Aviso de Privacidad fue actualizado por última vez el 18 de septiembre de 2026',
            },
          ],
        },
      ],
    },
    {
      id: 'definitions',
      title: '10. Definiciones',
      group: 6,
      blocks: [
        {
          kind: 'list',
          items: [
            { lead: 'Aviso de Privacidad:', text: 'Este documento.' },
            { lead: 'Cliente:', text: 'Restaurante o establecimiento que contrata los Productos de Parrot.' },
            {
              lead: 'Datos Personales:',
              text:
                'Cualquier información que concierne a una persona identificada o identificable. Se considera identificable a una persona cuya identidad pueda determinarse directa o indirectamente a través de cualquier información.',
            },
            {
              lead: 'Derechos ARCO:',
              text: 'Derechos de acceso, rectificación, cancelación y oposición al tratamiento de Datos Personales.',
            },
            { lead: 'Parrot:', text: 'S3 Software, S.A. de C.V.' },
            { lead: 'Ley:', text: 'Toda aquella legislación aplicable al tratamiento de Datos Personales.' },
            {
              lead: 'LFPDPPP:',
              text: 'Ley Federal de Protección de Datos Personales en Posesión de los Particulares.',
            },
            {
              lead: 'Persona Encargada (del Tratamiento):',
              text:
                'Persona física o moral que sola o conjuntamente con otras trate Datos Personales por cuenta de la Persona Responsable.',
            },
            {
              lead: 'Persona Responsable (del Tratamiento):',
              text:
                'Son los sujetos regulados, es decir, personas físicas o morales de carácter privado que llevan a cabo el tratamiento de Datos Personales.',
            },
            { lead: 'Persona Titular:', text: 'Es la persona a quien corresponden los Datos Personales.' },
            { lead: 'Persona Usuaria:', text: 'La persona que el Cliente da de alta para acceder a los Productos.' },
            {
              lead: 'Productos:',
              text:
                'Parrot POS, Parrot KDS, Parrot Pay y los demás productos que Parrot pone a disposición del Cliente.',
            },
            {
              lead: 'Sitio:',
              text: [
                'página web de Parrot disponible en el siguiente enlace: ',
                { text: 'https://parrotsoftware.com.mx/', href: 'https://parrotsoftware.com.mx/' },
              ],
            },
          ],
        },
      ],
    },
  ],
};
